import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { LanguageTestTemplateInput } from '../../../lib/types/models/language-test/language-test-template'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { createNumberArraySchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const schema: ZodType<LanguageTestTemplateInput> = z.object({
  name: createStringSchema('nom du test'),
  countries: createNumberArraySchema('pays', { isInteger: true, minimumNumberOfItems: 1 }),
  formulas: createNumberArraySchema('formules', { isInteger: true }).optional()
})

const LanguageTestTemplateForm = ({
  defaultValues,
  className,
  ...props
}: BaseFormProps<LanguageTestTemplateInput>) => {
  const form = useForm({
    mode: FORM_MODE,
    defaultValues,
    resolver: zodResolver(schema)
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(props.buttonsConfig))}>
        <div className={cn(className, 'flex flex-col gap-3')}>
          <FormField
            control={form.control}
            name={'name'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput {...field} required type={'text'} label={'Nom du test'} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'countries'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    dropDownPosition="above"
                    label={'Pays'}
                    variant={'primary'}
                    options={
                      countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((countryId) => ({
                        label:
                          countries?.data.find((country) => country.id === countryId)?.name ?? '',
                        value: countryId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'formulas'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    dropDownPosition="above"
                    label={'Formule(s)'}
                    variant={'primary'}
                    options={
                      formulas?.data.map((formula) => ({
                        label: getTranslatedName(formula),
                        value: formula.id
                      })) ?? []
                    }
                    searchable
                    selectedOptions={
                      field.value?.map((formulaId) => ({
                        label: getTranslatedName(
                          formulas?.data.find((formula) => formula.id === formulaId)
                        ),
                        value: formulaId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons hasBorderTop {...props} />
      </form>
    </Form>
  )
}

export default LanguageTestTemplateForm
